import React from 'react';
import styled, { keyframes } from 'styled-components';

const flowAnimation = keyframes`
  0% { stroke-dashoffset: 1000; }
  100% { stroke-dashoffset: 0; }
`;

const pulse = keyframes`
  0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(188, 19, 254, 0.4); }
  70% { transform: scale(1.05); box-shadow: 0 0 0 10px rgba(168, 85, 247, 0); }
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(168, 85, 247, 0); }
`;

const Container = styled.div`
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  padding: 40px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
`;

const FlowRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  position: relative;
  z-index: 2;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 60px;
  }
`;

const Node = styled.div<{ color: string; delay: string }>`
  width: 140px;
  height: 140px;
  background: rgba(20, 20, 30, 0.8);
  border: 1px solid ${props => props.color};
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  position: relative;
  backdrop-filter: blur(10px);
  animation: ${pulse} 3s infinite ${props => props.delay};
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 0 30px ${props => props.color}40;
  }
`;

const Icon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
`;

const Label = styled.div`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 700;
  font-size: 1rem;
  color: #ffffff;
  margin-bottom: 4px;
  text-align: center;
`;

const SubLabel = styled.div`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 400;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
`;

const Connector = styled.div`
  flex: 1;
  height: 2px;
  background: rgba(255, 255, 255, 0.1);
  position: relative;
  margin: 0 20px;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, #bc13fe, transparent);
    transform: translateX(-100%);
    animation: slideRight 2s linear infinite;
  }

  @keyframes slideRight {
    100% { transform: translateX(100%); }
  }

  @media (max-width: 768px) {
    width: 2px;
    height: 60px;
    margin: 0;
    
    &::after {
      background: linear-gradient(180deg, transparent, #bc13fe, transparent);
      transform: translateY(-100%);
      animation: slideDown 2s linear infinite;
    }
  }

  @keyframes slideDown {
    100% { transform: translateY(100%); }
  }
`;

export const SystemArchitecture = () => {
  return (
    <Container>
      <FlowRow>
        <Node color="#bc13fe" delay="0s">
          <Icon>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </Icon>
          <Label>Creator</Label>
          <SubLabel>Defines Token</SubLabel>
        </Node>

        <Connector />

        <Node color="#bc13fe" delay="0.5s">
          <Icon>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
            </svg>
          </Icon>
          <Label>Gelato Relay</Label>
          <SubLabel>Gas Sponsorship</SubLabel>
        </Node>

        <Connector />

        <Node color="#00f3ff" delay="1s">
          <Icon>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
            </svg>
          </Icon>
          <Label>Token Factory</Label>
          <SubLabel>Creates Markets</SubLabel>
        </Node>

        <Connector />

        <Node color="#0aff0a" delay="1.5s">
          <Icon>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 20h20" />
              <path d="M5 16c1.5-2 3-2 4.5 0s3 2 4.5 0 3-2 4.5 0" />
              <path d="M5 12c1.5-2 3-2 4.5 0s3 2 4.5 0 3-2 4.5 0" />
              <path d="M5 8c1.5-2 3-2 4.5 0s3 2 4.5 0 3-2 4.5 0" />
            </svg>
          </Icon>
          <Label>Live Trading</Label>
          <SubLabel>Synthetic Liquidity</SubLabel>
        </Node>
      </FlowRow>
    </Container>
  );
};
